import { useMemo } from "react";

import { useIntl } from "react-intl";

import { getFoodAndDrinkMessage } from "~/components/listings/full-service-venues/utils/fsv.utils";
import { useListingData } from "~/context/ListingPageDataContext";
import type { FsvDepositPriceBreakdown } from "~/types/fsv.types";
import { isMinimumSpendDepositListing } from "~/utils/pay-at-venue.utils";

import { useBookListingContext } from "../BookListingContext";
import { getFsvDepositPriceBreakdown } from "../fsv-deposit-price.utils";

type UseFsvDepositLayoutResult = {
  isDepositListing: boolean;
  shouldUseEstimate: boolean;
  depositBreakdown: FsvDepositPriceBreakdown | null;
  foodDrinkCategoryPhrase: string;
  showDepositLayout: boolean;
};

/**
 * Derives all FSV minimum-spend deposit display state shared across the
 * booking card header and price summary.
 */
export const useFsvDepositLayout = (): UseFsvDepositLayoutResult => {
  const intl = useIntl();
  const { data: listingData } = useListingData();
  const { quote, estimate } = useBookListingContext();

  const shouldUseEstimate = !quote && !!estimate;
  const isDepositListing = isMinimumSpendDepositListing(
    listingData?.payAtVenue,
  );

  const depositBreakdown = useMemo(
    () =>
      isDepositListing && listingData?.fullService?.active
        ? getFsvDepositPriceBreakdown({
            quote,
            estimate: estimate ?? null,
            useEstimate: shouldUseEstimate,
          })
        : null,
    [
      estimate,
      isDepositListing,
      listingData?.fullService?.active,
      quote,
      shouldUseEstimate,
    ],
  );

  const foodDrinkCategoryPhrase = getFoodAndDrinkMessage(
    listingData?.fullService?.drink_options,
    listingData?.fullService?.food_packages?.[0],
    intl,
  );

  const showDepositLayout = !!depositBreakdown && !!foodDrinkCategoryPhrase;

  return {
    isDepositListing,
    shouldUseEstimate,
    depositBreakdown,
    foodDrinkCategoryPhrase,
    showDepositLayout,
  };
};
